import { FSCollections } from '@src/constants/database';
import type {
  AuditLogQuery,
  AuditLogResponse,
} from '@src/models/interfaces/AuditLog/AuditLogInterface';
import type { ElasticQueryFilters } from '@src/models/interfaces/general/ElasticQueryParams.interface';
import { AuditLogMapper } from '@src/models/mappers/auditLog.mapper';
import AuditLogRepository from '@src/repositories/AuditLogRepository';

class AuditLogService {
  private readonly repository: AuditLogRepository;

  constructor(repository: AuditLogRepository = new AuditLogRepository()) {
    this.repository = repository;
  }

  async all({
    orgId,
    entityTypes = [],
    entityIds = [],
    pageSize = 10,
    lastPosition = 0,
    searchTerm = '',
  }: AuditLogQuery): Promise<AuditLogResponse> {
    // Do not filter actors in ES: `user` is nested, so a root-level `exists`
    // never matches valid USER_ACTIVITY rows. Invalid actors stay in the page
    // so pagination totals remain accurate; the table shows System Process.
    const filters: ElasticQueryFilters = {};

    if (entityTypes.length) {
      filters.terms = {
        ...filters.terms,
        entityType: entityTypes,
      };
    }

    if (entityIds.length) {
      filters.terms = {
        ...filters.terms,
        entityId: entityIds,
      };
    }

    const trimmedSearchTerm = searchTerm.trim();
    if (trimmedSearchTerm) {
      filters.textSearch = trimmedSearchTerm;
    }

    const response = await this.repository.all({
      entity: FSCollections.AUDITS,
      organizationId: orgId,
      filters,
      pagination: {
        from: lastPosition,
        size: pageSize,
      },
      sort: [{ field: 'createdAt', order: 'desc' }],
    });

    if (!response?.hits) {
      return { logs: [], total: 0 };
    }

    return {
      logs: response.hits.hits.map(AuditLogMapper.fromElasticHit),
      total: response.hits.total?.value ?? 0,
    };
  }
}

export default AuditLogService;
